import { useEffect } from 'react';
import styled from '@emotion/styled';
import { noop } from '@hotjar/webapp/lib/function';
import { useCurrentSiteId } from '@hotjar/site';
import { RETAKER_MODAL_HEADER_HEIGHT } from './constants';

export function getRecordingUrlForRetaker({
  siteId,
  recordingId,
  recordingStartTime,
}: {
  siteId: number;
  recordingId: number;
  recordingStartTime: number;
}): string {
  const recordingUrl = new URL(`${window.location.origin}/r`);

  recordingUrl.searchParams.append('forRetaker', 'true');
  recordingUrl.searchParams.append('site', siteId.toString());
  recordingUrl.searchParams.append('recording', recordingId.toString());
  recordingUrl.searchParams.append('startTime', recordingStartTime.toString());

  if (recordingUrl.protocol === 'http:') {
    recordingUrl.protocol = 'https:';
  }

  return recordingUrl.href;
}

const RECORDINGS_RETAKER_DATA_EVENT = 'hm_retaker_event';

const RecordingIframe = styled.iframe`
  display: block;
  padding: 0;
  margin: 0;
  width: 100%;
  border-width: 0;
  height: calc(100% - ${RETAKER_MODAL_HEADER_HEIGHT});
`;

interface RecordingRetakerEvent {
  event: string;
  payload: {
    currentTime: number;
    isLoaded?: boolean;
  };
}

interface RecordingsRetakerPlayerProps {
  setCurrentTime: (position: number) => void;
  setIsLoaded: (isLoaded: boolean) => void;
  recordingId?: number;
  recordingStartTime?: number;
}

export const RecordingsRetakerPlayer = ({
  recordingId,
  recordingStartTime,
  setCurrentTime = noop,
  setIsLoaded = noop,
}: RecordingsRetakerPlayerProps): JSX.Element | null => {
  const currentSiteId = useCurrentSiteId();

  const onMessage = (event: MessageEvent<RecordingRetakerEvent>): void => {
    if (
      event.origin !== window.location.origin ||
      event.data?.event !== RECORDINGS_RETAKER_DATA_EVENT
    ) {
      return;
    }

    if (!event.data?.payload) return;

    const { payload } = event.data;

    if (payload.currentTime) {
      setCurrentTime(payload.currentTime);
    } else if (payload.isLoaded) {
      setIsLoaded(true);
    }
  };

  useEffect(() => {
    window.addEventListener('message', onMessage, true);
    return () => {
      window.removeEventListener('message', onMessage);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!currentSiteId || !recordingId || !recordingStartTime) {
    return null;
  }

  return (
    <RecordingIframe
      data-testid="recording-iframe"
      id="recording-frame"
      title="recording"
      src={getRecordingUrlForRetaker({
        siteId: Number(currentSiteId),
        recordingId,
        recordingStartTime,
      })}
      sandbox="allow-same-origin allow-scripts"
    />
  );
};
